import { ElementRef } from '@angular/core';
import { AbstractControl, UntypedFormArray } from '@angular/forms';
import { TimeComponent, TimeString } from '../_models/time-component';
import { RegexValidator } from './regex.validator';
import { HttpHeaders } from '@angular/common/http';
import { Device } from '../_models/device';
import { InformationBannerType } from '../_models/information-banner';
import { Laboratory } from '../_models/laboratory';
import { Role } from '../_models/role';

const msPerSecond = 1000;
const secondsPerMinute = 60;
const minsPerHour = 60;
const hoursPerDay = 24;

// Uppercase letter, one lowercase letter, one number, and one special character (@#$%^&+!=)
export const passwordRegex =
  /(?=[A-Za-z0-9@#$%^&+!=]+$)^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+!=])(?=.{12,}).*$/;
export const phoneNumberRegex = '^[0-9 ()+-]+$';
export const nameRegex = '^([^0-9]*)$';
export const twoDecimalPlaceRegex = '^\\d+(?:\\.\\d{1,2})?$';
export const integerOnlyRegex = '^[0-9]*$';

// Additional name regex - used for calibration name and experiment name
export const advancedNameRegex = '^[\x20-\x7E]+$';

interface BufferOption {
  key: number | null;
  value: string;
  options?: string[];
}

export const pHBufferOptions: BufferOption[] = [
  { key: null, value: '--' },
  { key: 0, value: 'Custom' },
  { key: 1, value: 'pH 1.68' },
  { key: 2, value: 'pH 4.01' },
  { key: 3, value: 'pH 6.86' },
  { key: 4, value: 'pH 7.00' },
  { key: 5, value: 'pH 9.18' },
  { key: 6, value: 'pH 9.21' },
  { key: 7, value: 'pH 10.01' },
  { key: 8, value: 'pH 12.45' },
];

// Helper method to clear form array without breaking subscriptions to value changed
export const clearFormArray = (formArray: UntypedFormArray): void => {
  while (formArray.length !== 0) {
    formArray.removeAt(0);
  }
};

export function allDevicesHaveRequiredModules(
  devices: Device[],
  requiredModules: number[],
): boolean {
  return devices.every((device) =>
    requiredModules.every((module) => device.deviceModules.includes(module)),
  );
}

// Helper method to get remaining time
export const calculateTimeLeft = (
  startTime: Date | string | null | undefined,
  duration: number | null | undefined,
  currentTime: Date,
): TimeComponent | null => {
  if (startTime == null || duration == null) {
    return null;
  }

  const endDate = new Date(startTime);
  if (isNaN(endDate.getTime())) {
    return null;
  }

  endDate.setHours(endDate.getHours() + duration);
  const timeDifference = endDate.getTime() - currentTime.getTime();

  let milliseconds = Math.floor(timeDifference);
  let seconds = Math.floor((timeDifference / msPerSecond) % secondsPerMinute);
  let minutes = Math.floor(
    (timeDifference / (msPerSecond * secondsPerMinute)) % minsPerHour,
  );
  let hours = Math.floor(
    (timeDifference / (msPerSecond * secondsPerMinute * minsPerHour)) %
      hoursPerDay,
  );
  let days = Math.floor(
    timeDifference /
      (msPerSecond * secondsPerMinute * minsPerHour * hoursPerDay),
  );

  milliseconds = milliseconds > 0 ? milliseconds : 0;
  seconds = seconds > 0 ? seconds : 0;
  minutes = minutes > 0 ? minutes : 0;
  hours = hours > 0 ? hours : 0;
  days = days > 0 ? days : 0;

  return { milliseconds, seconds, minutes, hours, days };
};

export const getConvertedTimeFromMs = (timeinMs: number): TimeComponent => {
  let days = Math.floor(
    timeinMs / (msPerSecond * secondsPerMinute * minsPerHour * hoursPerDay),
  );
  let hours = Math.floor(
    (timeinMs / (msPerSecond * secondsPerMinute * minsPerHour)) % hoursPerDay,
  );
  let minutes =
    Math.floor(timeinMs / (msPerSecond * secondsPerMinute)) % minsPerHour;
  let seconds = Math.floor(timeinMs / msPerSecond) % secondsPerMinute;
  let milliseconds = Math.floor(timeinMs);

  // ET - TODO: have a look at handling negative time
  milliseconds = milliseconds > 0 ? milliseconds : 0;
  seconds = seconds > 0 ? seconds : 0;
  minutes = minutes > 0 ? minutes : 0;
  hours = hours > 0 ? hours : 0;
  days = days > 0 ? days : 0;

  return {
    milliseconds,
    seconds,
    minutes,
    hours,
    days,
  };
};

export const getConvertedMvFromReading = (reading: number): number => {
  const vMax = 3.3;
  const rawMax = 4096;
  const gain = 3.2;
  const vRef = 0.5103;

  if (reading == null) {
    return 0;
  }

  return Number(
    (((reading * vMax) / (rawMax * gain) - vRef) * 1000).toFixed(3),
  );
};

export const getConvertedTimeStringFromMs = (timeinMs: number): TimeString => {
  let days = Math.floor(
    timeinMs / (msPerSecond * secondsPerMinute * minsPerHour * hoursPerDay),
  );
  let hours = Math.floor(
    (timeinMs / (msPerSecond * secondsPerMinute * minsPerHour)) % hoursPerDay,
  );
  let minutes =
    Math.floor(timeinMs / (msPerSecond * secondsPerMinute)) % minsPerHour;
  let seconds = Math.floor(timeinMs / msPerSecond) % secondsPerMinute;
  let milliseconds = Math.floor(timeinMs);

  milliseconds = milliseconds > 0 ? milliseconds : 0;
  seconds = seconds > 0 ? seconds : 0;
  minutes = minutes > 0 ? minutes : 0;
  hours = hours > 0 ? hours : 0;
  days = days > 0 ? days : 0;

  const msString =
    milliseconds > 9
      ? milliseconds.toString()
      : '0' + milliseconds.toString().slice(-2);

  const secString =
    seconds > 9 ? seconds.toString() : '0' + seconds.toString().slice(-2);

  const minString =
    minutes > 9 ? minutes.toString() : '0' + minutes.toString().slice(-2);

  const hrString =
    hours > 9 ? hours.toString() : '0' + hours.toString().slice(-2);

  const dayString =
    days > 9 ? days.toString() : '0' + days.toString().slice(-2);

  return {
    milliseconds: msString,
    seconds: secString,
    minutes: minString,
    hours: hrString,
    days: dayString,
  };
};

export interface ValidationErrorMessages {
  required: () => string;
  requiredTrue: () => string;
  minlength: (par: { requiredLength: number }) => string;
  maxlength: (par: { requiredLength: number }) => string;
  mustMatch: () => string;
  email: () => string;
  pattern: (par: RegexValidator) => string;
  min: (par: { min: number; actual: string }) => string;
  minLength: (par: { minLength: number; actual: string }) => string;
  max: (par: { max: number; actual: string }) => string;
  maxLength: (par: { maxLength: number; actual: string }) => string;
  stepError: (par: { value: number; step: number }) => string;
  step: (par: { value: number; step: number }) => string;
  invalidNumber: () => string;
  integer: () => string;
  buffer: () => string;
  range: (par: { min: number; max: number; actual: string }) => string;
  notAscending: (par: {
    currentField: string;
    previousField: string;
    currentValue: number;
    previousValue: number;
  }) => string;
  invalidMacAddress: () => string;
  noWhitespace: () => string;
}

export const ERROR_MESSAGE: ValidationErrorMessages = {
  required: (): string => `Required field`,
  requiredTrue: (): string => `Please select at least one option`,
  minlength: (par: { requiredLength: number }): string =>
    `Field must be minimum ${par.requiredLength} characters long `,
  maxlength: (par: { requiredLength: number }): string =>
    `Field must be max ${par.requiredLength} characters long `,
  mustMatch: (): string => `Field does not match`,
  email: (): string => `Not a valid email address`,
  pattern: (par: RegexValidator): string => {
    switch (par.requiredPattern) {
      case passwordRegex.toString():
        return `Password must be at least 12 characters long, contain at least one
          uppercase letter, one lowercase letter, one number, and one special
          character (@#$%^&+!=)`;
      case phoneNumberRegex:
        return 'Not a valid phone number';
      case nameRegex:
        return 'Field cannot contain numbers';
      case advancedNameRegex:
        return 'Field can only contain characters ASCII 0x20-0x7E';
      case twoDecimalPlaceRegex:
        return 'Maximum of 2 decimal places allowed';
      case integerOnlyRegex:
        return 'Only integers allowed';
      default:
        return 'Not valid';
    }
  },
  min: (par: { min: number; actual: string }): string => {
    return `Must be a minimum of ${par.min}`;
  },
  minLength: (par: { minLength: number; actual: string }): string => {
    return `Must be a minimum length of ${par.minLength}`;
  },
  max: (par: { max: number; actual: string }): string => {
    return `Must be a maximum of ${par.max}`;
  },
  maxLength: (par: { maxLength: number; actual: string }): string => {
    return `Must be a maximum length of ${par.maxLength}`;
  },
  stepError: (par: { value: number; step: number }): string => {
    return `Value must be a multiple of ${par.step}`;
  },
  step: (par: { value: number; step: number }): string => {
    return `Value must be a multiple of ${par.step}`;
  },
  invalidNumber: (): string => `Value must be a valid number`,
  integer: (): string => {
    return `Value must be an integer`;
  },
  buffer: (): string => {
    return `Buffer already selected`;
  },
  range: (par: { min: number; max: number; actual: string }): string => {
    return `The input value is outside of the range [${par.min}, ${par.max}]`;
  },
  notAscending: (par: {
    currentField: string;
    previousField: string;
    currentValue: number;
    previousValue: number;
  }): string => {
    return `Value ${par.currentValue} must be greater than or equal to ${par.previousField} value (${par.previousValue})`;
  },
  invalidMacAddress: (): string => `Not a valid MAC address`,
  noWhitespace: (): string => `Field cannot be whitespace only`,
};

export const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }),
  observe: 'response' as const,
};

export const passesLengthValidation = (text: string): boolean => {
  if (!text) return false;
  return text?.length >= 12;
};

export const passesNumberValidation = (text: string): boolean => {
  if (!text) return false;
  return /\d/.test(text);
};

export const passesSpecialCharValidation = (text: string): boolean => {
  if (!text) return false;
  return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(text);
};

export const passesUpperCaseValidation = (text: string): boolean => {
  if (!text) return false;
  return /[A-Z]/.test(text);
};

export const passesLowerCaseValidation = (text: string): boolean => {
  if (!text) return false;
  return /[a-z]/.test(text);
};

export const getLongestNameLength = (arr: Device[]): number => {
  if (!Array.isArray(arr)) return 0;

  return arr.reduce((maxLength, obj) => {
    const nameLength = typeof obj.name === 'string' ? obj.name.length : 0;
    return Math.max(maxLength, nameLength);
  }, 0);
};

export const bannerIcon = (iconType: InformationBannerType): string => {
  switch (iconType) {
    case InformationBannerType.Information:
    case InformationBannerType.Positive:
      return 'information';
    case InformationBannerType.Warning:
    case InformationBannerType.Critical:
      return 'warning';
    default:
      return 'information';
  }
};

export function handleDigitFocus(event: FocusEvent): void {
  (event.target as HTMLInputElement).select();
}

export function handleDigitInput(
  index: number,
  event: Event,
  inputs: readonly ElementRef<HTMLInputElement>[],
): void {
  const value = (event.target as HTMLInputElement).value;
  if (!value) return;
  if (index < inputs.length - 1) {
    inputs[index + 1].nativeElement.focus();
  }
}

export function handleDigitKeydown(
  event: KeyboardEvent,
  index: number,
  inputs: readonly ElementRef<HTMLInputElement>[],
  controls: AbstractControl[],
): void {
  if (
    event.key.length === 1 &&
    !/\d/.test(event.key) &&
    !event.ctrlKey &&
    !event.metaKey
  ) {
    event.preventDefault();
    return;
  }
  if (event.key !== 'Backspace') return;
  const current = inputs[index].nativeElement;
  if (!current.value && index > 0) {
    controls[index - 1].setValue('');
    inputs[index - 1].nativeElement.focus();
  }
}

export function handleDigitPaste(
  event: ClipboardEvent,
  inputs: readonly ElementRef<HTMLInputElement>[],
  controls: AbstractControl[],
): void {
  event.preventDefault();
  const text = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
  const chars = text.slice(0, 6).split('');
  controls.forEach((control, i) => control.setValue(chars[i] ?? ''));
  const lastIndex = Math.min(chars.length, inputs.length - 1);
  inputs[lastIndex].nativeElement.focus();
}

export const orderModules = (arr: number[]): number[] => {
  return arr.sort((a, b) => {
    // 1. Move 5 to the very front (Batch Culture)
    if (a === 5 && b !== 5) return -1;
    if (b === 5 && a !== 5) return 1;

    // 2. Move 3 to the second position (Liquid Control)
    if (a === 3 && b !== 3) return -1;
    if (b === 3 && a !== 3) return 1;

    // 3. For all other numbers, sort them normally (ascending)
    return a - b;
  });
};

/**
 * Resolves a lab's isOwnLab against the authoritative currentUser.laboratories
 * list, since the lab emitted by selectedLab$ or loaded from storage may not
 * have isOwnLab populated (or may be stale). Callers that gate fetches or
 * templates on isOwnLab should resolve through this rather than trusting the
 * incoming lab's own isOwnLab value directly.
 */
export const resolveLabOwnership = (
  lab: Laboratory | null,
  laboratories: Laboratory[] | null | undefined,
): Laboratory | null => {
  if (!lab) {
    return lab;
  }
  const ownLab = laboratories?.find((x) => x.laboratoryId === lab.laboratoryId);
  return { ...lab, isOwnLab: ownLab?.isOwnLab ?? false };
};

/**
 * Whether the given (already-resolved, see resolveLabOwnership) lab's data is
 * fetchable/viewable for this role. Super_User is restricted to labs they are
 * actually a member of; every other role just needs a lab to be selected.
 * This is the single source of truth for the fetch guards and the dashboard/
 * template-list "you are not a member of this lab" gating — duplicating it
 * inline per component is what caused a prior regression (an inverted role
 * check that let the wrong view render for the wrong role).
 */
export const canAccessLabData = (
  lab: Laboratory | null,
  role: Role | null | undefined,
): boolean => !!lab && !!role && (role !== Role.Super_User || !!lab.isOwnLab);
